import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useRef, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { api, ApiError, fieldIssues } from '../api';
import { can, type Me } from '../auth/me';
import { RowMenu } from '../ui/RowMenu';
import { SYSTEM_KIND_LABELS, SYSTEMS_KEY, SYSTEMS_RESOURCE, useSystems, type SystemItem } from './systems';
import { leaveGuarded, sameValues, useOpening, useUnsavedGuard } from '../ui/unsaved';

type Contractor = { id: string; name: string; kind: 'client' | 'supplier' | 'both' };
type Block = { id: string; name: string };
type Draft = { name: string; kind: 'own' | 'external'; contractorId: string; blockId: string; description: string };

const emptyDraft = (): Draft => ({ name: '', kind: 'own', contractorId: '', blockId: '', description: '' });
const draftOf = (s: SystemItem): Draft => ({ name: s.name, kind: s.kind === 'external' ? 'external' : 'own', contractorId: s.contractorId ?? '', blockId: s.blockId ?? '', description: s.description });

function Problem({ error }: { error: unknown }) {
  if (!(error instanceof ApiError)) return null;
  return (
    <p className="alert" role="alert">
      {error.status === 409 ? 'Ktoś już zmienił ten system. Zamknij i otwórz listę jeszcze raz.' : fieldIssues(error).map((i) => i.message).join(' ') || error.message}
    </p>
  );
}

/** One line under the name: what the system is. */
function about(s: SystemItem): string {
  if (s.kind === 'none') return 'Stała pozycja listy';
  return [SYSTEM_KIND_LABELS[s.kind], s.contractorName ? `dostawca: ${s.contractorName}` : '', s.blockName ? `blok: ${s.blockName}` : '', s.description].filter(Boolean).join(' · ');
}

/** Add or edit form; a system from outside has a supplier, ours may have a block for the packlist. */
function SystemForm({ me, base, onDone }: { me: Me; base: SystemItem | null; onDone: () => void }) {
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState<Draft>(base ? draftOf(base) : emptyDraft());
  const set = (patch: Partial<Draft>) => setDraft((d) => ({ ...d, ...patch }));
  const seeContractors = can(me, 'contractors.contractors', 'view');
  const seeBlocks = can(me, 'templates.blocks', 'view');
  const contractors = useQuery({ queryKey: ['contractors', 'system-supplier'], enabled: seeContractors && draft.kind === 'external', queryFn: () => api<{ contractors: Contractor[] }>('/api/contractors') });
  const blocks = useQuery({ queryKey: ['templates', 'blocks', 'system'], enabled: seeBlocks && draft.kind === 'own', queryFn: () => api<{ blocks: Block[] }>('/api/templates/blocks') });
  const suppliers = (contractors.data?.contractors ?? []).filter((c) => c.kind !== 'client');
  const others = (contractors.data?.contractors ?? []).filter((c) => c.kind === 'client');
  const save = useMutation({
    mutationFn: () => {
      const body = {
        name: draft.name.trim(),
        kind: draft.kind,
        description: draft.description.trim(),
        contractorId: draft.kind === 'external' && draft.contractorId ? draft.contractorId : null,
        blockId: draft.kind === 'own' && draft.blockId ? draft.blockId : null,
      };
      return base ? api(`/api/events/systems/${base.id}`, { method: 'PATCH', body: { version: base.version, ...body } }) : api('/api/events/systems', { method: 'POST', body });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: SYSTEMS_KEY });
      onDone();
    },
  });
  function submit(e: FormEvent) {
    e.preventDefault();
    e.stopPropagation();
    if (draft.name.trim() !== '') save.mutate();
  }
  // owner 2026-10-23 21:14: leaving with unsaved changes asks „Zapisz / Nie zapisuj / Anuluj”
  const opening = useOpening(draft);
  const guardRef = useUnsavedGuard({
    dirty: !sameValues(draft, opening),
    save: async () => {
      if (draft.name.trim() === '') return false;
      await save.mutateAsync();
      return true;
    },
  });
  return (
    <form ref={guardRef} className="settings-form" aria-label={base ? `Edycja systemu: ${base.name}` : 'Nowy system'} onSubmit={submit}>
      <div className="field-row">
        <label className="field grow">
          <span>Nazwa systemu</span>
          <input autoFocus value={draft.name} onChange={(e) => set({ name: e.target.value })} required autoComplete="off" placeholder={draft.kind === 'external' ? 'np. L-Acoustics L2 — Szmyt' : 'np. Nasz L-Acoustics Kara'} />
        </label>
        <fieldset className="radio-seg-field compact">
          <legend>Rodzaj</legend>
          <div className="radio-seg">
            {(['own', 'external'] as const).map((k) => (
              <label key={k} className={draft.kind === k ? 'on' : undefined}>
                <input type="radio" name="system-kind" value={k} checked={draft.kind === k} onChange={() => set({ kind: k })} />
                {SYSTEM_KIND_LABELS[k]}
              </label>
            ))}
          </div>
        </fieldset>
      </div>
      {draft.kind === 'external' && (
        <label className="field">
          <span>Dostawca</span>
          {seeContractors ? (
            <select value={draft.contractorId} onChange={(e) => set({ contractorId: e.target.value })}>
              <option value="">— bez dostawcy —</option>
              {suppliers.length > 0 && (
                <optgroup label="Dostawcy">
                  {suppliers.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </optgroup>
              )}
              {others.length > 0 && (
                <optgroup label="Pozostali kontrahenci">
                  {others.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </optgroup>
              )}
            </select>
          ) : (
            <span className="muted">Nie widzisz kontrahentów, więc dostawcę wpisze ktoś inny.</span>
          )}
          {seeContractors && (
            <span className="hint field-hint">
              Nie ma go na liście? Dodaj go w <Link to="/kontrahenci">Kontrahentach</Link> (rodzaj „dostawca”).
            </span>
          )}
        </label>
      )}
      {draft.kind === 'own' && seeBlocks && (
        <label className="field">
          <span>Blok do sprzętówki (opcjonalnie)</span>
          <select value={draft.blockId} onChange={(e) => set({ blockId: e.target.value })}>
            <option value="">— bez bloku —</option>
            {(blocks.data?.blocks ?? []).map((b) => (
              <option key={b.id} value={b.id}>
                {b.name}
              </option>
            ))}
          </select>
          <span className="hint field-hint">
            Sprzętówka wydarzenia z tym systemem podpowie wstawienie bloku. Bloki zakładasz w <Link to="/szablony/bloki">Szablony → Bloki sprzętu</Link>.
          </span>
        </label>
      )}
      <label className="field">
        <span>Opis (opcjonalnie)</span>
        <input value={draft.description} onChange={(e) => set({ description: e.target.value })} autoComplete="off" placeholder={draft.kind === 'external' ? 'np. odbiór dzień wcześniej z Gdyni' : ''} />
      </label>
      <Problem error={save.error} />
      <div className="actions">
        <button className="btn primary" type="submit" disabled={save.isPending || draft.name.trim() === ''}>
          {save.isPending ? 'Zapisywanie…' : base ? 'Zapisz zmiany' : 'Dodaj system'}
        </button>
        <button className="btn" type="button" onClick={onDone}>
          Anuluj
        </button>
      </div>
    </form>
  );
}

/**
 * The systems list (owner remark 3/13) — behind the gear next to the "System" field and in "Ustawienia firmy".
 * Links lead to where the parts of a system live: blocks (ours) and contractors (suppliers).
 */
export function SystemsSettings({ me, heading = true }: { me: Me; heading?: boolean }) {
  const queryClient = useQueryClient();
  const list = useSystems();
  const [editing, setEditing] = useState<string | 'new' | null>(null);
  // "+ Dodaj" / "Edytuj" ask only about this list's own form, not about the screen under it (the wizard, when the
  // list is open in its dialog: the gear next to the field)
  const sectionRef = useRef<HTMLElement>(null);
  const switchTo = (next: string | 'new') => leaveGuarded(() => setEditing(next), sectionRef.current);
  const [confirm, setConfirm] = useState<SystemItem | null>(null);
  const remove = useMutation({
    mutationFn: (s: SystemItem) => api(`/api/events/systems/${s.id}?version=${s.version}`, { method: 'DELETE' }),
    onSuccess: async () => {
      setConfirm(null);
      await queryClient.invalidateQueries({ queryKey: SYSTEMS_KEY });
    },
  });
  const canCreate = can(me, SYSTEMS_RESOURCE, 'create');
  const canEdit = can(me, SYSTEMS_RESOURCE, 'edit');
  const canDelete = can(me, SYSTEMS_RESOURCE, 'delete');
  const live = (list.data?.systems ?? []).filter((s) => !s.deleted);
  return (
    <section ref={sectionRef} className="settings-section" aria-label="Systemy" data-feature-id="events.systems">
      {heading && <h2>Systemy</h2>}
      <p className="hint">
        Lista do pola „System” wydarzenia: nasze systemy i te z zewnątrz (np. L-Acoustics L2 od Szmyta).
        {can(me, 'templates.blocks', 'view') && (
          <>
            {' '}
            <Link to="/szablony/bloki">Szablony</Link>
          </>
        )}
        {can(me, 'contractors.contractors', 'view') && (
          <>
            {' · '}
            <Link to="/kontrahenci">Kontrahenci</Link>
          </>
        )}
      </p>
      {list.error && <p className="alert" role="alert">{list.error instanceof ApiError ? list.error.message : 'Nie udało się wczytać listy systemów'}</p>}
      <ul className="settings-list" aria-label="Lista systemów">
        {live.map((s) =>
          editing === s.id ? (
            <li key={s.id}>
              <SystemForm me={me} base={s} onDone={() => setEditing(null)} />
            </li>
          ) : (
            <li key={s.id} className="settings-row">
              <div className="settings-row-main">
                <strong>{s.name}</strong>
                <span className="muted">{about(s)}</span>
              </div>
              {confirm?.id === s.id ? (
                <span className="settings-confirm" role="group" aria-label={`Usunąć system ${s.name}?`}>
                  <span>Usunąć?</span>
                  <button className="btn small danger" type="button" disabled={remove.isPending} onClick={() => remove.mutate(s)}>
                    Usuń
                  </button>
                  <button className="btn small ghost" type="button" onClick={() => setConfirm(null)}>
                    Nie
                  </button>
                </span>
              ) : (
                s.kind !== 'none' && (
                  <RowMenu
                    label={`Działania: ${s.name}`}
                    actions={[
                      ...(canEdit ? [{ label: 'Edytuj', onSelect: () => switchTo(s.id) }] : []),
                      ...(canDelete ? [{ label: 'Usuń', danger: true, onSelect: () => setConfirm(s) }] : []),
                    ]}
                  />
                )
              )}
            </li>
          ),
        )}
      </ul>
      <Problem error={remove.error} />
      {canCreate && editing === 'new' && <SystemForm me={me} base={null} onDone={() => setEditing(null)} />}
      {canCreate && editing !== 'new' && (
        <button className="btn small" type="button" onClick={() => switchTo('new')}>
          + Dodaj system
        </button>
      )}
      <p className="hint">Usunięty system znika z wyboru; wydarzenia, które go mają, zachowują go.</p>
    </section>
  );
}
